import * as React from 'react'
import * as Moment from 'moment'
import { minutesToTime } from '../helpers/time'
import CommentButton from './CommentButton'
import { OverviewUserShape, ResourcesOverviewTagShape } from '../../api/api_data_types'
import { getTextColorForBackground } from '../helpers/color'

type RowUserDataCell = {
  norm: number | string
  registered: number | string
  absence: number | string
  planned: number | string
  available_time: number | string
  absence_code: string
  comment?: string
  tag?: ResourcesOverviewTagShape | null
  external_installer_time?: number | null
}

type RowUserData = Record<string, RowUserDataCell>

type UserRowProps = {
  range: Moment.Moment[]
  userData: RowUserData
  user: OverviewUserShape
  isHolidayOrWeekend: (date: Moment.Moment) => boolean
  showNormTime: boolean
  canManageExternalInstallers?: boolean
  onExternalInstallerClick?: (() => void) | null
  canManageCellTags?: boolean
  onCellTagClick?: ((args: { date: string; tag: ResourcesOverviewTagShape | null; externalInstallerTime: number | null }) => void) | null
  pendingCellTagDate?: string | null
}

export default function UserRow({
  range,
  userData,
  user,
  isHolidayOrWeekend,
  showNormTime,
  canManageExternalInstallers = false,
  onExternalInstallerClick = null,
  canManageCellTags = false,
  onCellTagClick = null,
  pendingCellTagDate = null,
}: UserRowProps) {
  const cellClasses = (date: Moment.Moment) => {
    const cssClasses = []
    if (isHolidayOrWeekend(date)) {
      cssClasses.push('resources-overview-holiday')
    }
    if (date.date() === 16) {
      cssClasses.push('resources-overview-middle')
    }
    if (date.date() === 1) {
      cssClasses.push('resources-overview-beginning')
    }

    return cssClasses
  }

  const renderUserLink = () => {
    if (user.external_installer) {
      if (canManageExternalInstallers) {
        return (
          <button
            type="button"
            className="btn btn-link p-0 align-baseline"
            onClick={onExternalInstallerClick || undefined}
          >
            {user.name}
          </button>
        )
      }

      return <span>{user.name}</span>
    }

    return (
      <a href={`/?pretend_to_be=${user.id}`} target="_blank" rel="noreferrer">
        {user.name}
      </a>
    )
  }

  return (
    <tr className="resources-overview-user-row">
      <td className="resources-overview-username">{renderUserLink()}</td>
      {range.map((date) => {
        const key = date.format('YYYY-MM-DD')
        const dailyData = userData[key]
        if (!dailyData) {
          return (
            <React.Fragment key={key}>
              <td className={cellClasses(date).join(' ')} />
              <td />
            </React.Fragment>
          )
        }

        const hasExternalInstallerTime = user.external_installer && dailyData.external_installer_time != null
        const availableTime = hasExternalInstallerTime
          ? dailyData.external_installer_time || 0
          : (showNormTime ? dailyData.norm : dailyData.available_time)
        const timeTitle =
          `Normal: ${minutesToTime(dailyData.norm || '')}` +
          `\r\nPlanlagt: ${minutesToTime(dailyData.planned || '')}` +
          `\r\nPlanlagt fravær: ${minutesToTime(dailyData.absence || '')}` +
          `\r\nRegistreret: ${minutesToTime(dailyData.registered || '')}` +
          (hasExternalInstallerTime ? `\r\nManuel tid: ${minutesToTime(dailyData.external_installer_time || 0)}` : '')

        const innerCellClasses = cellClasses(date)
          .filter((c) => c === 'resources-overview-holiday') // do not need -middle and -beginning classes
          .join(' ')
        const canOpenTagPopup = canManageCellTags && Boolean(onCellTagClick)
        const isCellTagUpdating = pendingCellTagDate === key
        const valueCellClassName = [
          'resources-overview-value-cell',
          innerCellClasses,
          canOpenTagPopup && !isCellTagUpdating ? 'resources-overview-cell-tag-clickable' : '',
        ]
          .filter(Boolean)
          .join(' ')
        const valueCellStyle = dailyData.tag
          ? {
              backgroundColor: dailyData.tag.color,
              color: getTextColorForBackground(dailyData.tag.color),
            }
          : undefined

        return (
          <React.Fragment key={key}>
            <td className={cellClasses(date).join(' ')}>{dailyData.absence_code}</td>
            <td
              className={valueCellClassName}
              title={timeTitle}
              style={valueCellStyle}
              onClick={() => {
                if (!canOpenTagPopup || !onCellTagClick || isCellTagUpdating) return
                onCellTagClick({
                  date: key,
                  tag: dailyData.tag || null,
                  externalInstallerTime: dailyData.external_installer_time ?? null,
                })
              }}
            >
              {isCellTagUpdating ? <i className="fa fa-spinner fa-spin" /> : minutesToTime(availableTime)}
              <span
                onClick={(e) => {
                  e.stopPropagation()
                }}
              >
                <CommentButton userData={dailyData} user={user} date={date} />
              </span>
            </td>
          </React.Fragment>
        )
      })}
    </tr>
  )
}
